/**
 * Keeping every cached calendar range honest (`111-frontend-architecture.md` §3.3).
 *
 * The same event is very often rendered in TWO cached ranges at once — the agenda and the
 * month, say. Patching only the range you happen to be looking at produces a checkbox that
 * un-ticks itself when you switch views, so every optimistic write here walks **all** of them
 * with `getQueriesData` on the `['calendar']` prefix.
 *
 * ── Why the delivery-time invalidation is a prefix, not `111`'s overlap predicate ─────────
 *
 * `111` §3.3 sketches `invalidateOverlappingRanges(qc, intervals)`, which invalidates only
 * the ranges an event's interval intersects. Two things make the prefix the better trade
 * here, and the same document already reaches for it once:
 *
 *  - The invalidation that matters happens when the OUTBOX delivers the write, not when the
 *    mutation is queued (queuing resolves before the network — a refetch then would return a
 *    server copy that does not yet contain the change and would clobber the optimistic
 *    patch). The outbox bridge is where delivery lands, and it holds an entry, not an event:
 *    a `DELETE` and a `complete` carry no dates at all, so the interval is not knowable there.
 *  - `111` itself says of page due dates: "we invalidate the whole `['calendar']` prefix
 *    rather than trying to work out which ranges overlap — calendar ranges are few and cheap."
 *    At most three are ever cached at once. The same reasoning applies to every event write.
 *
 * `intervalOf` stays, because the OPTIMISTIC path genuinely does need it: a newly created
 * event must be inserted into exactly the cached ranges it belongs to and no others.
 */
import type { QueryClient } from '@tanstack/react-query';
import { keys } from '@/api/keys';
import type { CalendarEvent, CalendarResponse } from '@/api/endpoints/calendar';
import { effectiveEnd, startOfDayKey, zoned } from '@/features/calendar/datetime';

export interface Interval {
  /** Epoch ms. */
  start: number;
  end: number;
}

/** The interval an event OCCUPIES. A point event is one millisecond wide, never zero. */
export function intervalOf(event: CalendarEvent, tz: string): Interval {
  const start = zoned(event.startsAt, tz).getTime();
  const end = effectiveEnd(event, tz).getTime();
  return { start, end: Math.max(end, start + 1) };
}

/** The `[from, to)` a cached range key stands for, as instants. `to` is exclusive (`040`). */
function rangeOf(key: readonly unknown[], tz: string): Interval | null {
  const [scope, kind, from, to] = key as string[];
  if (scope !== 'calendar' || kind !== 'range') return null;
  if (typeof from !== 'string' || typeof to !== 'string') return null;

  return { start: startOfDayKey(from, tz).getTime(), end: startOfDayKey(to, tz).getTime() };
}

/** The server's own predicate (`080` §2.1), client-side: overlap, never containment. */
function overlapsRange(interval: Interval, range: Interval): boolean {
  return interval.start < range.end && interval.end > range.start;
}

/**
 * Apply `patch` to every cached range, having first decided per range whether the event
 * belongs there. Returns the snapshots, for rollback.
 */
export function patchCachedRanges(
  queryClient: QueryClient,
  patch: (response: CalendarResponse, range: Interval) => CalendarResponse,
  tz: string,
): [readonly unknown[], CalendarResponse | undefined][] {
  const entries = queryClient.getQueriesData<CalendarResponse>({ queryKey: keys.calendar.all() });

  for (const [key] of entries) {
    const range = rangeOf(key, tz);
    if (range === null) continue;

    queryClient.setQueryData<CalendarResponse>(key, (old) =>
      old === undefined ? old : patch(old, range),
    );
  }

  return entries;
}

export function restoreCachedRanges(
  queryClient: QueryClient,
  previous: readonly [readonly unknown[], CalendarResponse | undefined][],
): void {
  for (const [key, data] of previous) queryClient.setQueryData(key, data);
}

/** Insert an event into a range's response iff its interval overlaps that range. */
export function upsertEvent(
  response: CalendarResponse,
  event: CalendarEvent,
  range: Interval,
  tz: string,
): CalendarResponse {
  const belongs = overlapsRange(intervalOf(event, tz), range);
  const without = response.events.filter((existing) => existing.id !== event.id);

  return { ...response, events: belongs ? [...without, event] : without };
}

/** Find an event in ANY cached range — it is very often in several at once. */
export function findEvent(queryClient: QueryClient, id: string): CalendarEvent | undefined {
  for (const [, data] of queryClient.getQueriesData<CalendarResponse>({
    queryKey: keys.calendar.all(),
  })) {
    const hit = data?.events.find((event) => event.id === id);
    if (hit !== undefined) return hit;
  }
  return undefined;
}
